/**
 * PHARMAKON — S1 « Nouvel achat » configuration (refonte WP-22; design/ux/02_achats.md §4).
 *
 * Value lists and bounds come from the browser twin of the server CONFIG
 * (`@pharmakon/shared/constants/achat-saisie.js`, parity-tested against the validator) and from the
 * existing client configs (product type labels, SHP values) — never typed here (PK-BUG-15).
 */
import {
  LIMITES_SAISIE_ACHAT,
  SOURCE_PPA_SUGGERE,
  SOURCE_PRIX_SUGGERE,
  TAUX_TVA_ACHAT,
  TYPE_ACHAT_VALUES,
} from "@pharmakon/shared/constants/achat-saisie.js";
import { TYPES_PRODUIT_OFFICINE } from "@pharmakon/shared/constants/produit-type-champs.js";
import { TYPE_PRODUIT_LABELS } from "./app.config.js";
import { SHP_OPTIONS } from "./shp.config.js";

// Refonte WP-65 (D-27, A-105) — the sources of the PU HT / PPA S1 suggests (GET /achats/prix-suggeres).
export { LIMITES_SAISIE_ACHAT, SOURCE_PPA_SUGGERE, SOURCE_PRIX_SUGGERE, TAUX_TVA_ACHAT };

/** D-14 — the « Mixte » type (several product types on one facture). */
export const TYPE_ACHAT_MIXTE = "MIXTE";

/** D-14 — today's default of a new facture (NouvelAchat.jsx:1633 before the refonte). */
export const TYPE_ACHAT_DEFAUT = "MEDICAMENT";

/** D-14 — the label of the « Mixte » type d'achat (it has no row in the officine's type catalogue). */
export const LIBELLE_TYPE_ACHAT_MIXTE = "Mixte";

/**
 * The TypeAchat values (the predefined types and MIXTE). Refonte WP-68 (A-117): the « Type d'achat »
 * options are the officine's catalogue (`lib/types-produit.js` `optionsTypes`), never this list.
 */
export { TYPE_ACHAT_VALUES };

/** The label of a predefined type d'achat / product type value (an owner type: `libelleCle` of lib/types-produit.js). */
export const libelleType = (type) =>
  type === TYPE_ACHAT_MIXTE ? LIBELLE_TYPE_ACHAT_MIXTE : TYPE_PRODUIT_LABELS[type] ?? type ?? "";

/** TVA options of the entry band (ux/02 §4.7 #9). */
export const TVA_OPTIONS = Object.freeze(TAUX_TVA_ACHAT.map((t) => Object.freeze({ value: String(t), label: `${t} %` })));

/** SHP options (ux/02 §4.7 #11, values of config/shp.config.js — AUD-ACHNEW-17: no hand-written tooltip). */
export const SHP_SELECT_OPTIONS = Object.freeze(
  SHP_OPTIONS.map((v) => Object.freeze({ value: String(v), label: v.toFixed(2).replace(".", ",") })),
);

/**
 * A-03 — péremption per product type: hidden for Matériel médical and Orthopédie (an undated lot
 * stores the existing « Sans péremption » sentinel); optional for Consommable, Complément
 * alimentaire and Cosmétique; required otherwise (Médicament, Parapharmacie, unknown type).
 */
export const PEREMPTION_MODE = Object.freeze({ REQUISE: "REQUISE", OPTIONNELLE: "OPTIONNELLE", MASQUEE: "MASQUEE" });
export const PEREMPTION_MASQUEE_POUR = Object.freeze(["MATERIEL_MEDICAL", "ORTHOPEDIE"]);
export const PEREMPTION_OPTIONNELLE_POUR = Object.freeze(["CONSOMMABLE", "COMPLEMENT_ALIMENTAIRE", "COSMETIQUE"]);

/**
 * ux/02 §4.7 « Enter chain » — the fields of the entry band in Entrée order (the order of
 * `COL_ORDER`, NouvelAchat.jsx:124-135, adapted). Qté reçue is the last: Entrée there adds the line.
 * `ug_pct` is in the chain only while UG is 0; `date_peremption` only when shown (A-03).
 */
export const CHAMPS_SAISIE = Object.freeze([
  "produit",
  "quantite",
  "ug",
  "ug_pct",
  "numero_lot",
  "date_peremption",
  "prix_achat_ht",
  "ristourne",
  "taux_tva",
  // Refonte WP-69 (D-27) — only on a line whose type takes a margin (non-médicament), right before
  // its « Prix de vente »; a Médicament line skips it (no margin input, PPA as before).
  "marge_pct",
  "prix_vente_ppa",
  "shp",
  "quantite_recue",
]);

/**
 * Refonte WP-69 (D-27, A-13; défaut A-120) — the three fields the margin links on a non-médicament
 * line, in band order: PU HT, Marge %, Prix de vente. The two typed last are kept, the third is
 * computed by the shared engine (`prixVenteDepuisMarge` / `prixAchatDepuisMarge` / `margePct`).
 */
export const CHAMPS_PRIX_LIES = Object.freeze(["prix_achat_ht", "marge_pct", "prix_vente_ppa"]);

/**
 * Refonte WP-69 — bounds of the margin typed in the band: the type catalogue's (`TYPES_PRODUIT_OFFICINE`,
 * the shared twin of the server's `TYPE_PRODUIT_OFFICINE_BORNES`) — never below 0 % (a Prix de vente
 * under the PU HT is refused, M-S1-20) and never past what a type's default margin may hold.
 */
export const MARGE_SAISIE = Object.freeze({
  MIN: TYPES_PRODUIT_OFFICINE.MARGE_PCT_MIN,
  MAX: TYPES_PRODUIT_OFFICINE.MARGE_PCT_MAX,
});

/** ux/01 §3.6 / 02 §4.4 (OP-ACH-17) — the B1 panel is docked from 1 632 px of content (a 1 680 px zone). */
export const ACHAT_SAISIE_DOCK_AT = 1632;

/** ux/02 §4.17 — local safety copy of the facture being typed (keys of NouvelAchat.jsx:67-69, kept). */
export const BROUILLON_LOCAL = Object.freeze({
  CLE: "pharmakon_achat_draft",
  cle: (id) => (id ? `pharmakon_achat_draft:edit:${id}` : "pharmakon_achat_draft:new"),
  /** Version of the stored shape: a copy written by another shape is never restored. */
  VERSION: 2,
  /** « every 30 s and on each line add » (ux/02 §4.17). */
  INTERVALLE_MS: 30_000,
});

/** ux/02 §4.7 — the product picker of the entry band: rows asked per query (A-43 limit 15). */
export const PRODUIT_PICKER_LIMITE = 15;

/** Debounce of the FEFO-inversion hint of the entry band (the legacy check, non-blocking). */
export const FEFO_VERIFICATION_DELAI_MS = 400;
